import React from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthPageLayout } from '../components/AuthPageLayout';
import { supabase } from '../lib/supabaseClient';
import { invalidateMeCache } from '../lib/meClient';
import { resolvePostLoginPath, takeReturnPath } from '../lib/postLoginRedirect';

function toGerman(message: string): string {
  const m = message.toLowerCase();
  if (m.includes('password should be at least') || m.includes('password must be at least'))
    return 'Das Passwort muss mindestens 8 Zeichen lang sein.';
  if (m.includes('weak password')) return 'Das Passwort ist zu schwach. Bitte wähle ein stärkeres.';
  if (m.includes('auth session missing') || m.includes('no session'))
    return 'Sitzung abgelaufen. Bitte fordere eine neue Einladung an.';
  if (m.includes('network') || m.includes('fetch'))
    return 'Netzwerkfehler. Bitte prüfe deine Internetverbindung.';
  return message;
}

export function CompleteProfilePage() {
  const navigate = useNavigate();
  const [fullName, setFullName] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [passwordConfirm, setPasswordConfirm] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    supabase.auth.getUser().then(({ data }) => {
      const name = data?.user?.user_metadata?.full_name as string | undefined;
      if (name) setFullName(name);
    });
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!fullName.trim()) {
      setError('Bitte gib deinen vollständigen Namen ein.');
      return;
    }
    if (password.length < 8) {
      setError('Das Passwort muss mindestens 8 Zeichen lang sein.');
      return;
    }
    if (password !== passwordConfirm) {
      setError('Die Passwörter stimmen nicht überein.');
      return;
    }

    setLoading(true);
    try {
      const { error: updateError } = await supabase.auth.updateUser({
        password,
        data: { full_name: fullName.trim() },
      });
      if (updateError) throw new Error(updateError.message);

      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !sessionData.session) throw new Error('Keine aktive Sitzung nach Profil-Update.');

      const res = await fetch('/api/auth/login', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ access_token: sessionData.session.access_token }),
      });
      const payload = (await res.json().catch(() => null)) as
        | { activeInstance?: { id?: string }; error?: string }
        | null;
      if (!res.ok) throw new Error(payload?.error ?? 'Login fehlgeschlagen.');

      invalidateMeCache();
      const instanceId = String(payload?.activeInstance?.id ?? '');
      const next = takeReturnPath(new URLSearchParams(window.location.search).get('next'));
      navigate(resolvePostLoginPath(instanceId, next), { replace: true });
    } catch (err) {
      const raw = err instanceof Error ? err.message : String(err);
      setError(toGerman(raw));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthPageLayout>
      <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-50 border border-blue-100 mb-5">
        <div className="w-2 h-2 rounded-full bg-blue-500" />
        <span className="text-xs font-medium text-blue-700">Du wurdest eingeladen</span>
      </div>

      <h1 className="text-2xl font-bold text-slate-900">Profil einrichten</h1>
      <p className="text-sm text-slate-500 mt-1 leading-relaxed">
        Gib deinen Namen und ein Passwort ein, um deinen Account zu aktivieren.
      </p>

      <form onSubmit={(e) => { void handleSubmit(e); }} className="mt-6 space-y-4">
        <label className="block">
          <span className="block text-xs font-medium text-slate-600 mb-1.5">Vollständiger Name</span>
          <input
            type="text"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            required
            autoComplete="name"
            autoFocus={!fullName}
            className="w-full border border-app-border rounded-xl px-3.5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#3b82f6]/25 focus:border-[#3b82f6] transition-all"
            placeholder="Max Mustermann"
          />
        </label>

        <label className="block">
          <span className="block text-xs font-medium text-slate-600 mb-1.5">Passwort wählen</span>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={8}
            autoComplete="new-password"
            className="w-full border border-app-border rounded-xl px-3.5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#3b82f6]/25 focus:border-[#3b82f6] transition-all"
            placeholder="Mindestens 8 Zeichen"
          />
        </label>

        <label className="block">
          <span className="block text-xs font-medium text-slate-600 mb-1.5">Passwort bestätigen</span>
          <input
            type="password"
            value={passwordConfirm}
            onChange={(e) => setPasswordConfirm(e.target.value)}
            required
            minLength={8}
            autoComplete="new-password"
            className={`w-full border rounded-xl px-3.5 py-2.5 text-sm outline-none focus:ring-2 transition-all ${
              passwordConfirm && password !== passwordConfirm
                ? 'border-red-300 focus:ring-red-200 focus:border-red-400'
                : 'border-app-border focus:ring-[#3b82f6]/25 focus:border-[#3b82f6]'
            }`}
            placeholder="Passwort wiederholen"
          />
          {passwordConfirm && password !== passwordConfirm && (
            <span className="mt-1 block text-xs text-red-500">Passwörter stimmen nicht überein.</span>
          )}
        </label>

        {error ? (
          <div className="rounded-xl bg-red-50 border border-red-200 px-3.5 py-3">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        ) : null}

        <button
          type="submit"
          disabled={loading || !fullName.trim() || password.length < 8 || password !== passwordConfirm}
          className="w-full rounded-xl bg-[#3b82f6] px-4 py-3 text-sm font-semibold text-white hover:bg-blue-600 disabled:opacity-60 transition-colors shadow-sm mt-2"
        >
          {loading ? 'Account wird aktiviert…' : 'Account aktivieren'}
        </button>
      </form>
    </AuthPageLayout>
  );
}
